import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import {
  AlertTriangle,
  Trash2,
  ArrowLeft,
  Shield,
  FileText,
  Heart,
  MessageSquare,
  Activity,
  Trophy,
  Loader2,
  CheckCircle,
  XCircle,
  Download,
  Users,
  Bell,
  Key,
  Brain,
  Flame
} from "lucide-react";

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [deletionInfo, setDeletionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [reason, setReason] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState({
    permanent: false,
    noRecovery: false,
    understood: false
  });

  useEffect(() => {
    fetchDeletionInfo();
  }, []);

  const fetchDeletionInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/deletion-info`, {
        withCredentials: true
      });
      setDeletionInfo(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error("Failed to load deletion information");
      }
    } finally {
      setLoading(false);
    }
  };

  const allAcknowledged = acknowledged.permanent && acknowledged.noRecovery && acknowledged.understood;
  const confirmationValid = confirmationText === "DELETE";

  const handleDelete = async () => {
    if (!confirmationValid || !allAcknowledged) return;

    setDeleting(true);
    try {
      await axios.post(
        `${API}/auth/delete-account`,
        { confirmation: confirmationText, reason: reason || null },
        { withCredentials: true }
      );
      
      toast.success("Your account has been deleted. A confirmation email has been sent.");
      
      // Log out and redirect
      setTimeout(async () => {
        await logout();
        navigate("/");
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
      setDeleting(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      "Profile": Shield,
      "Journal Entries": FileText,
      "Mood & Energy Data": Heart,
      "Community Posts": MessageSquare,
      "Wellness Data": Activity,
      "Progress Data": Trophy,
      "Gamification": Trophy,
      "AI Conversations": Brain,
      "Partnerships": Users,
      "Notifications": Bell,
      "Sessions": Key,
      "Transaction Records": FileText
    };
    return icons[category] || FileText;
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await axios.post(`${API}/user/export-data`, 
        { date_range: "all_time" },
        {
          withCredentials: true,
          responseType: "blob"
        }
      );
      
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `compass-data-export-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Your data has been downloaded successfully");
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Export limit reached. Please try again in 24 hours.");
      } else if (error.response?.status === 400) {
        toast.error("No data found to export. You can proceed with deletion.");
      } else {
        toast.error("Failed to export data. Please try again.");
      }
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f5f9f5] to-[#e8f0e8] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#5a8a5a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f9f5] to-[#e8f0e8] p-4 md:p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate("/profile")}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            data-testid="back-to-profile"
          >
            <ArrowLeft className="w-5 h-5 text-[#5a8a5a]" />
          </button>
          <div>
            <h1 className="text-2xl font-heading text-[#5a8a5a]">Delete Account</h1>
            <p className="text-sm text-[#a69373]/60">
              Permanently remove your account and data
            </p>
          </div>
        </div>

        {/* Warning Banner */}
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex gap-4">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-700">This action cannot be undone</h3>
              <p className="text-sm text-red-600 mt-1">
                Deleting your account will permanently remove all your data. 
                Please read the information below carefully before proceeding.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Information */}
        {step === 1 && (
          <>
            {/* Data to be deleted */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-[#5a8a5a] flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  Data That Will Be Deleted
                </CardTitle>
                <CardDescription>
                  The following data will be permanently removed from our systems
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {deletionInfo?.data_to_delete?.map((item, index) => {
                  const Icon = getCategoryIcon(item.category);
                  return (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-3 bg-red-50/50 rounded-lg border border-red-100"
                    >
                      <Icon className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-[#a69373]">{item.category}</p>
                        <p className="text-sm text-[#a69373]/60">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Data retained */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-[#5a8a5a] flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-500" />
                  Data That Will Be Retained
                </CardTitle>
                <CardDescription>
                  Some records must be kept for legal compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                {deletionInfo?.data_retained?.map((item, index) => {
                  const Icon = getCategoryIcon(item.category);
                  return (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-3 bg-amber-50/50 rounded-lg border border-amber-100"
                    >
                      <Icon className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-[#a69373]">{item.category}</p>
                        <p className="text-sm text-[#a69373]/60">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Export recommendation card */}
            <Card className="glass-card border-[#5a8a5a]/30 bg-[#5a8a5a]/5">
              <CardContent className="p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <Download className="w-8 h-8 text-[#5a8a5a] flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-[#5a8a5a]">Export your data first</h3>
                  <p className="text-sm text-[#a69373]/70 mt-1">
                    Download a copy of your journal entries and reflections before deleting your account. This data cannot be recovered after deletion.
                  </p>
                </div>
                <Button 
                  onClick={handleExportData}
                  disabled={exportLoading}
                  className="bg-[#5a8a5a] hover:bg-[#4a7a4a] text-white whitespace-nowrap"
                  data-testid="export-before-delete"
                >
                  {exportLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export My Data
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Button 
              onClick={() => setStep(2)}
              variant="destructive"
              className="w-full"
              data-testid="proceed-to-confirmation"
            >
              I Understand, Proceed to Confirmation
            </Button>
          </>
        )}

        {/* Step 2: Confirmation */}
        {step === 2 && (
          <>
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-red-600">
                  Confirm Account Deletion
                </CardTitle>
                <CardDescription>
                  Please acknowledge the following and type the confirmation text
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Acknowledgements */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="permanent"
                      checked={acknowledged.permanent}
                      onCheckedChange={(checked) => 
                        setAcknowledged(prev => ({ ...prev, permanent: !!checked }))
                      }
                      data-testid="ack-permanent"
                    />
                    <Label htmlFor="permanent" className="text-sm text-[#a69373] leading-relaxed cursor-pointer">
                      I understand that account deletion is <strong>permanent</strong> and 
                      all my data will be removed immediately.
                    </Label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="noRecovery"
                      checked={acknowledged.noRecovery}
                      onCheckedChange={(checked) => 
                        setAcknowledged(prev => ({ ...prev, noRecovery: !!checked }))
                      }
                      data-testid="ack-no-recovery"
                    />
                    <Label htmlFor="noRecovery" className="text-sm text-[#a69373] leading-relaxed cursor-pointer">
                      I understand that my data <strong>cannot be recovered</strong> after deletion.
                    </Label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="understood"
                      checked={acknowledged.understood}
                      onCheckedChange={(checked) => 
                        setAcknowledged(prev => ({ ...prev, understood: !!checked }))
                      }
                      data-testid="ack-understood"
                    />
                    <Label htmlFor="understood" className="text-sm text-[#a69373] leading-relaxed cursor-pointer">
                      I have read and understood the data deletion policy above.
                    </Label>
                  </div>
                </div>

                {/* Optional reason */}
                <div className="space-y-2">
                  <Label htmlFor="reason" className="text-[#a69373]">
                    Why are you leaving? (optional)
                  </Label>
                  <Textarea
                    id="reason"
                    placeholder="Help us improve by sharing your feedback..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="resize-none"
                    rows={3}
                    data-testid="deletion-reason"
                  />
                </div>

                {/* Confirmation text */}
                <div className="space-y-2">
                  <Label htmlFor="confirmation" className="text-[#a69373]">
                    Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
                  </Label>
                  <Input
                    id="confirmation"
                    type="text"
                    placeholder="DELETE"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                    className={confirmationValid ? "border-green-500" : ""}
                    data-testid="confirmation-input"
                  />
                  {confirmationText && !confirmationValid && (
                    <p className="text-xs text-red-500">
                      Please type exactly: DELETE
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button 
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
                data-testid="back-to-info"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDelete}
                disabled={!confirmationValid || !allAcknowledged || deleting}
                className="flex-1"
                data-testid="delete-account-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete My Account
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {/* Cancel option */}
        <div className="text-center">
          <button
            onClick={() => navigate("/profile")}
            className="text-sm text-[#5a8a5a] hover:underline"
            data-testid="cancel-deletion"
          >
            Cancel and return to profile
          </button>
        </div>
      </div>
    </div>
  );
}
